import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';

import { Global, ThemeProvider, css } from '@emotion/react';
import { ThemeMode } from '@partoohub/modular-components';
import { darkTheme, theme as lightTheme } from '@partoohub/ui';

import env from 'app/common/services/getEnvironment';
import { globalWithScrollbarStyling } from 'app/global.styled';

import { usePartooTheme } from './hooks/usePartooTheme';
import { useThemeDetector } from './hooks/useThemeDetector';

type ThemeContextType = {
    theme: string;
    updateTheme: (mode: ThemeMode) => void;
};

const PartooTheme = createContext<ThemeContextType | undefined>(undefined);

export const usePartooThemeContext = () => {
    const context = useContext(PartooTheme);
    if (!context) {
        throw new Error('useThemeContext must be used within a PartooThemeProvider');
    }
    return context;
};

export const useIsDarkTheme = (): boolean => {
    const { theme } = usePartooThemeContext();

    return theme === ThemeMode.DARK;
};

export const USE_DARK_THEME = env.isDev() || env.isStaging();

export const getSelectedTheme = (
    isDarkBrowserTheme: boolean,
    useDarkTheme: boolean,
    partooTheme?: ThemeMode,
) => {
    if (!useDarkTheme || !partooTheme) return lightTheme;

    switch (partooTheme) {
        case ThemeMode.SYSTEM:
            return isDarkBrowserTheme ? darkTheme : lightTheme;
        case ThemeMode.DARK:
            return darkTheme;
        default:
            return lightTheme;
    }
};

/**
 * Handle everything related to the theme of the app
 */
export const PartooThemeProvider: FC<PropsWithChildren> = ({ children }: PropsWithChildren) => {
    const isDarkBrowserTheme = useThemeDetector();
    const [partooTheme, setPartooTheme] = usePartooTheme();

    const selectedTheme = getSelectedTheme(isDarkBrowserTheme, USE_DARK_THEME, partooTheme);

    const updateTheme = (mode: ThemeMode) => {
        setPartooTheme(mode);
    };

    const value = useMemo(
        () => ({
            theme: partooTheme,
            updateTheme,
        }),
        [partooTheme],
    );

    return (
        <PartooTheme.Provider value={value}>
            {/* Global css styles */}
            <Global styles={globalWithScrollbarStyling(selectedTheme)} />

            {/*
                Theme specific style
                Couldn't provide selectedTheme to globalStyle because of performance issues
            */}
            <Global
                styles={css`
                    // For info, there is the same applied on ReactPageContainer in ReactPage.styled.ts
                    body {
                        background: ${selectedTheme.colors.theme.backgroundTheme};
                    }
                `}
            />
            <ThemeProvider theme={selectedTheme}>{children}</ThemeProvider>
        </PartooTheme.Provider>
    );
};
